import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { $TOWNS } from '@components/$TOWNS/$TOWNS'
import { useStakingDeposits } from '@components/ClaimRewardsPanel/hooks/useStakingData'
import { ErrorReportForm } from '@components/ErrorReport/ErrorReport'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { Panel } from '@components/Panel/Panel'
import { PanelButton } from '@components/Panel/PanelButton'
import { Box, Button, FancyButton, Icon, IconButton, IconName, Paragraph, Stack, Text } from '@ui'
import { DAY_MS, HOUR_MS, MINUTE_MS } from 'data/constants'
import { formatDistance } from 'date-fns'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { useDevice } from 'hooks/useDevice'
import { useIsTradingEnabled } from 'hooks/useTradingEnabled'
import { ReauthenticateButton, WalletReady } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { useStakingRewardsQuery } from 'use-towns-client'
import { formatUnits } from 'viem'

import { DetectUndeployedSimpleAccount } from '../DetectUndeployedSimpleAccount/DetectUndeployedSimpleAccount'
import { NftsList } from '../Wallet/NftsList'
import { StakedTokensTab } from '../Wallet/StakedTokensTab'

import { useTradingWallet } from './hooks/useTradingWallet'
import { useTradingWalletAddresses } from './hooks/useTradingWalletAddresses'
import { TabPanel } from './ui/TabPanel'
import { AutoCreateSolanaWallet } from './AutoCreateSolanaWallet'
import { StakeToTownBanner } from './StakeToTownBanner'
import { TradingActivity } from './TradingActivity'
import { TradingTokensList } from './TradingTokensList'
import { calculateTotalHoldingValueCents, formatCents } from './tradingUtils'

enum ModalType {
    ReportBug = 'report-bug',
}

export function TradingWalletPanel() {
    const { openPanel, data: panelData } = usePanelActions()
    const { isTouch } = useDevice()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const [modal, setModal] = useState<ModalType | undefined>(undefined)

    const { data: currentReward } = useStakingRewardsQuery({
        beneficiary: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    const {
        data: { groupedDeposits },
    } = useStakingDeposits({
        owner: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    const totalStaked = useMemo(() => {
        return groupedDeposits.reduce((acc, deposit) => {
            return acc + deposit.totalStaked.toBigInt()
        }, 0n)
    }, [groupedDeposits])

    // Parse navigation data from panel transitions
    const navigationData = useMemo(() => {
        if (!panelData) {
            return null
        }
        try {
            return JSON.parse(panelData)
        } catch {
            return null
        }
    }, [panelData])

    const onClickDeposit = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.TRADING_DEPOSIT, { forceStack: true })
    }, [openPanel])

    const onClickSend = () => {
        openPanel(CHANNEL_INFO_PARAMS.TRADING_SEND, { forceStack: true })
    }

    const onClickClaimRewards = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.CLAIM_REWARDS, { forceStack: true })
    }, [openPanel])

    const { evmWalletAddress } = useTradingWalletAddresses()
    const {
        data: chainWalletAssets,
        isLoading: isLoadingChainWalletAssets,
        error: assetsError,
        invalidateWalletQuery,
        lastUpdated,
    } = useTradingWallet()

    const totalHoldingValueCents = useMemo(() => {
        return calculateTotalHoldingValueCents(chainWalletAssets)
    }, [chainWalletAssets])

    const onTransferAsset = () => {
        openPanel('transfer-assets', { assetSource: evmWalletAddress })
    }

    const { isTradingEnabled } = useIsTradingEnabled()

    const holdingValue24hAgo = useMemo(() => {
        if (!chainWalletAssets) {
            return 0
        }
        // Calculate value from 24h ago by using price change percentage
        // We cap negative price changes at -99% to avoid division by zero
        // and to handle cases where price data might be incorrect
        return chainWalletAssets.reduce((acc, asset) => {
            const nativePct = Math.max(-0.99, asset.nativeAsset.priceChange24h)
            const value24hAgo = asset.nativeAsset.holdingValueCents / (1 + nativePct)

            const tokensValue24hAgo = asset.tokens.reduce((acc, token) => {
                const tokenPct = Math.max(-0.99, token.priceChange24h)
                return acc + token.holdingValueCents / (1 + tokenPct)
            }, 0)
            return acc + value24hAgo + tokensValue24hAgo
        }, 0)
    }, [chainWalletAssets])

    const gain24h = totalHoldingValueCents - holdingValue24hAgo
    const gain24hPct = holdingValue24hAgo === 0 ? 0 : (gain24h / holdingValue24hAgo) * 100

    const onTabChange = useCallback((tab: string) => {
        setTab(tab)
    }, [])

    const tabs = useMemo(
        () => [
            { label: 'Tokens', value: 'tokens', disabled: !isTradingEnabled },
            { label: 'Staking', value: 'staked', disabled: false },
            { label: 'Activity', value: 'activity' },
            { label: 'NFTs', value: 'nfts', disabled: !isTradingEnabled },
        ],
        [isTradingEnabled],
    )

    const [tab, setTab] = useState(() => {
        // Check if we have navigation data specifying a tab
        if (navigationData?.activeTab) {
            return navigationData.activeTab
        }
        return tabs.find((t) => !t.disabled)?.value
    })

    // Update tab when navigation data changes
    useEffect(() => {
        if (navigationData?.activeTab && tabs.some((t) => t.value === navigationData.activeTab)) {
            setTab(navigationData.activeTab)
        }
    }, [navigationData, tabs])

    const onBugReportClick = useCallback(() => {
        setModal(ModalType.ReportBug)
    }, [])

    const rightBarButton = isTouch ? (
        <IconButton icon="bug" background="none" color="default" onClick={onBugReportClick} />
    ) : undefined

    return (
        <Panel padding isRootPanel={isTouch} label="Wallet" rightBarButton={rightBarButton}>
            <WalletReady
                LoginButton={
                    <>
                        <Stack gap alignItems="center">
                            <Text color="gray2">Reauthenticate to view your wallet</Text>
                            <ReauthenticateButton />
                        </Stack>
                    </>
                }
            >
                {() => (
                    <>
                        <AutoCreateSolanaWallet />
                        <DetectUndeployedSimpleAccount />

                        <Stack grow gap="md" paddingTop="md">
                            <LastUpdatedBanner
                                invalidateWalletQuery={invalidateWalletQuery}
                                lastUpdated={lastUpdated.getTime()}
                                isLoading={isLoadingChainWalletAssets}
                            />

                            {!assetsError && (
                                <Box gap alignItems="center">
                                    <Text fontSize="h1" fontWeight="strong" textAlign="center">
                                        {formatCents(totalHoldingValueCents)}
                                    </Text>
                                    <Text size="lg" fontWeight="strong" color="gray2">
                                        +<$TOWNS value={totalStaked} currency="usd" /> Staked
                                    </Text>
                                    <Stack
                                        horizontal
                                        gap
                                        alignSelf="center"
                                        color={gain24hPct > 0 ? 'greenBlue' : 'error'}
                                    >
                                        <Text>{formatCents(gain24h)}</Text>
                                        <Text>{gain24hPct.toFixed(2)}%</Text>
                                    </Stack>
                                </Box>
                            )}

                            <Stack gap>
                                <Stack horizontal gap centerContent>
                                    <WalletActionButton
                                        iconName="plus"
                                        label="Deposit"
                                        onClick={onClickDeposit}
                                    />
                                    <WalletActionButton
                                        iconName="linkOut"
                                        label="Send"
                                        onClick={onClickSend}
                                    />
                                </Stack>

                                {currentReward && currentReward.gt(1n * 10n ** 16n) && (
                                    <RewardsBanner
                                        rewardAmount={currentReward.toBigInt()}
                                        onClaim={onClickClaimRewards}
                                    />
                                )}

                                <StakeToTownBanner />
                            </Stack>

                            <TabPanel
                                grow
                                layoutId="tradingTokensList"
                                value={tab}
                                tabs={tabs}
                                onChange={onTabChange}
                            >
                                <Stack gap grow paddingTop="md">
                                    {tab === 'tokens' && (
                                        <TradingTokensList
                                            assets={chainWalletAssets ?? []}
                                            assetsError={assetsError}
                                            isLoading={isLoadingChainWalletAssets}
                                        />
                                    )}
                                    {tab === 'nfts' && (
                                        <Stack gap grow>
                                            <PanelButton cursor="pointer" onClick={onTransferAsset}>
                                                <Icon
                                                    type="linkOutWithFrame"
                                                    size="square_sm"
                                                    color="gray2"
                                                />
                                                <Text strong>Transfer Asset</Text>
                                            </PanelButton>
                                            <NftsList walletAddress={evmWalletAddress} />
                                        </Stack>
                                    )}
                                    {tab === 'activity' && <TradingActivity />}
                                    {tab === 'staked' && (
                                        <StakedTokensTab newDeposit={navigationData?.newDeposit} />
                                    )}
                                </Stack>
                            </TabPanel>
                        </Stack>
                    </>
                )}
            </WalletReady>

            {modal === ModalType.ReportBug && (
                <ModalContainer touchTitle="Bug Report" onHide={() => setModal(undefined)}>
                    <Box padding gap>
                        <ErrorReportForm onHide={() => setModal(undefined)} />
                    </Box>
                </ModalContainer>
            )}
        </Panel>
    )
}

const WalletActionButton = ({
    iconName,
    label,
    onClick,
}: {
    iconName: IconName
    label: string
    onClick: () => void
}) => {
    return (
        <PanelButton
            grow
            centerContent
            background="level2"
            padding="md"
            height="x10"
            width="x12"
            rounded="md"
            onClick={onClick}
        >
            <Box direction="column" alignItems="center" gap="sm">
                <Icon type={iconName} size="square_sm" color="cta2" />
                <Text
                    size="md"
                    color="gray2"
                    paddingBottom="xs"
                    paddingTop="xxs"
                    fontWeight="medium"
                >
                    {label}
                </Text>
            </Box>
        </PanelButton>
    )
}

const LastUpdatedBanner = (props: {
    invalidateWalletQuery: () => void
    lastUpdated: number
    isLoading: boolean
}) => {
    const { invalidateWalletQuery, lastUpdated, isLoading } = props

    const [distance, setDistance] = useState(() => Date.now() - lastUpdated)

    useEffect(() => {
        let timeout: NodeJS.Timeout
        let raf: number
        function update() {
            const distance = Date.now() - lastUpdated

            setDistance(distance)

            if (distance < 0) {
                return
            }
            const timeframe =
                distance < HOUR_MS ? MINUTE_MS : distance < DAY_MS * 2 ? HOUR_MS : DAY_MS

            const nextTick = timeframe - (distance % timeframe)
            timeout = setTimeout(() => {
                raf = requestAnimationFrame(update)
            }, nextTick)
        }
        update()
        return () => {
            clearTimeout(timeout)
            cancelAnimationFrame(raf)
        }
    }, [lastUpdated])

    return (
        <Stack centerContent horizontal gap="sm">
            <Paragraph size="xs" color="gray2">
                Updated {formatDistance(0, distance, { addSuffix: true, includeSeconds: false })}
            </Paragraph>
            <Button
                border="level3"
                size="button_xs"
                rounded="full"
                disabled={isLoading}
                tone="level1"
                onClick={invalidateWalletQuery}
            >
                <Stack horizontal centerContent gap="xs">
                    <Box centerContent aspectRatio="1/1" height="x2" width="x2" color="gray2">
                        {isLoading ? (
                            <ButtonSpinner width="x1" height="x1" />
                        ) : (
                            <Icon type="arrowCircle" size="square_xs" />
                        )}
                    </Box>
                    <Text size="xs" color="gray2" fontWeight="normal">
                        Update
                    </Text>
                </Stack>
            </Button>
        </Stack>
    )
}

const RewardsBanner = ({
    rewardAmount,
    onClaim,
}: {
    rewardAmount: bigint
    onClaim: () => void
}) => {
    const rewardAmountRef = useRef(rewardAmount)
    rewardAmountRef.current = rewardAmount

    // Track when user sees the claim rewards prompt (only once)
    useEffect(() => {
        if (rewardAmountRef.current) {
            Analytics.getInstance().track('claim rewards prompt', {
                rewardAmount: Number(formatUnits(rewardAmountRef.current, 18)),
            })
        }
    }, [])

    // Track when user clicks the claim button
    const handleClaimClick = useCallback(() => {
        Analytics.getInstance().track('clicked claim rewards prompt', {
            rewardAmount: Number(formatUnits(rewardAmount, 18)),
        })
        onClaim()
    }, [onClaim, rewardAmount])

    return (
        <Box
            horizontal
            gap
            borderRadius="md"
            paddingX="md"
            paddingY="md"
            display="flex"
            alignItems="center"
            justifyContent="spaceBetween"
            width="100%"
            style={{ minHeight: 64 }}
            background="level2"
        >
            <Box centerContent borderRadius="full" width="x4" height="x6">
                <Icon type="diamond" size="square_lg" color="cta2" />
            </Box>
            <Stack grow gap="paragraph">
                <Text fontWeight="strong" color="default" size="lg">
                    You earned <$TOWNS value={rewardAmount} />
                </Text>
                <Text color="gray2" size="sm">
                    Claim and stake it now
                </Text>
            </Stack>
            <FancyButton cta compact borderRadius="full" onClick={handleClaimClick}>
                Claim
            </FancyButton>
        </Box>
    )
}
